import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { UserCog, Plus, Star, Briefcase, MapPin, Edit, Trash2, Phone, Mail, DollarSign, Clock, Calendar, Save, X, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function HandymenManagement() {
  const [handymen, setHandymen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedHandyman, setSelectedHandyman] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    skills: '',
    working_hours: '9AM-6PM',
    off_days: '',
    hourly_rate: 25
  });

  useEffect(() => {
    fetchHandymen();
  }, []);

  const fetchHandymen = async () => {
    try {
      const response = await axiosInstance.get('/handymen');
      setHandymen(response.data);
    } catch (error) {
      toast.error('Failed to load handymen');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      skills: '',
      working_hours: '9AM-6PM',
      off_days: '',
      hourly_rate: 25
    });
    setShowPassword(false);
  };

  const handleAddHandyman = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const skills = formData.skills.split(',').map(s => s.trim()).filter(s => s);
      const offDays = formData.off_days.split(',').map(d => d.trim()).filter(d => d);
      
      await axiosInstance.post('/handymen', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        skills,
        off_days: offDays,
        working_hours: formData.working_hours,
        hourly_rate: parseFloat(formData.hourly_rate) || 25
      });
      toast.success('Handyman added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchHandymen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add handyman');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (handyman) => {
    setSelectedHandyman(handyman);
    setFormData({
      name: handyman.name || '',
      email: handyman.email || '',
      phone: handyman.phone || '',
      password: '', // Don't show existing password
      skills: Array.isArray(handyman.skills) ? handyman.skills.join(', ') : (handyman.skills || ''),
      working_hours: typeof handyman.working_hours === 'object' 
        ? `${handyman.working_hours?.start || '9AM'}-${handyman.working_hours?.end || '6PM'}`
        : (handyman.working_hours || '9AM-6PM'),
      off_days: Array.isArray(handyman.off_days) ? handyman.off_days.join(', ') : (handyman.off_days || ''),
      hourly_rate: handyman.hourly_rate || 25
    });
    setShowEditDialog(true);
  };

  const handleEditHandyman = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const skills = formData.skills.split(',').map(s => s.trim()).filter(s => s);
      const offDays = formData.off_days.split(',').map(d => d.trim()).filter(d => d);
      
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        skills,
        off_days: offDays,
        working_hours: formData.working_hours,
        hourly_rate: parseFloat(formData.hourly_rate) || 25
      };
      
      // Only include password if provided
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password;
      }
      
      await axiosInstance.put(`/handymen/${selectedHandyman.id}`, updateData);
      toast.success('Handyman updated successfully');
      setShowEditDialog(false);
      setSelectedHandyman(null);
      resetForm();
      fetchHandymen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update handyman');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHandyman = async () => {
    setSaving(true);
    try {
      await axiosInstance.delete(`/handymen/${selectedHandyman.id}`);
      toast.success('Handyman deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedHandyman(null);
      fetchHandymen();
    } catch (error) {
      toast.error('Failed to delete handyman');
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = async (handyman) => {
    try {
      await axiosInstance.patch(`/handymen/${handyman.id}/availability`, {
        is_available: !handyman.is_available
      });
      toast.success(`${handyman.name} is now ${!handyman.is_available ? 'available' : 'unavailable'}`);
      fetchHandymen();
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const formatWorkingHours = (hours) => {
    if (typeof hours === 'object') {
      return `${hours?.start || '9AM'} - ${hours?.end || '6PM'}`;
    }
    return hours || '9AM-6PM';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Handymen Management</h1>
          <p className="text-gray-600">Manage your technicians and their assignments</p>
        </div>
        <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-handyman-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Handyman
        </Button>
      </div>

      {/* Handymen Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {handymen.map((handyman) => (
          <Card key={handyman.id} className="hover:shadow-lg transition-shadow" data-testid={`handyman-card-${handyman.id}`}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <UserCog className="w-6 h-6 text-[#595959]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{handyman.name}</CardTitle>
                    <p className="text-sm text-gray-500">ID: {handyman.id?.slice(0, 8)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(handyman)}
                    className="h-8 w-8 p-0"
                    data-testid={`edit-handyman-${handyman.id}`}
                  >
                    <Edit className="w-4 h-4 text-gray-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedHandyman(handyman); setShowDeleteConfirm(true); }}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    data-testid={`delete-handyman-${handyman.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Star className="w-4 h-4 text-yellow-500" />
                <span>{handyman.rating || 0}/5.0</span>
                <span className="text-gray-400">({handyman.total_jobs || 0} jobs)</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-[#595959]" />
                <span>{handyman.email}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-[#595959]" />
                <span>{handyman.phone || 'No phone'}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-[#595959]" />
                <span>{formatWorkingHours(handyman.working_hours)}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <DollarSign className="w-4 h-4 text-[#595959]" />
                <span>${handyman.hourly_rate || 25}/hour</span>
              </div>
              
              {handyman.skills && (Array.isArray(handyman.skills) ? handyman.skills : [handyman.skills]).length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2">
                  {(Array.isArray(handyman.skills) ? handyman.skills : handyman.skills.split(',')).map((skill, idx) => (
                    <span key={idx} className="bg-gray-200 text-[#595959] px-2 py-1 rounded text-xs font-medium">
                      {typeof skill === 'string' ? skill.trim() : skill}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex items-center justify-between pt-3 border-t">
                <Badge className={handyman.is_available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {handyman.is_available ? 'Available' : 'Unavailable'}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAvailability(handyman)}
                  data-testid={`toggle-availability-${handyman.id}`}
                >
                  {handyman.is_available ? 'Mark Unavailable' : 'Mark Available'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {handymen.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserCog className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No handymen yet</h3>
            <p className="text-gray-400 text-sm">Add your first technician to get started</p>
          </CardContent>
        </Card>
      )}

      {/* Add Handyman Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#595959]" />
              Add New Handyman
            </DialogTitle>
            <DialogDescription>
              Create a new technician account with login credentials.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddHandyman} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  required
                  data-testid="handyman-name-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                  data-testid="handyman-email-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 555-123-4567"
                  data-testid="handyman-phone-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Create password"
                    required
                    data-testid="handyman-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>Working Hours</Label>
                <Input
                  value={formData.working_hours}
                  onChange={(e) => setFormData({...formData, working_hours: e.target.value})}
                  placeholder="9AM-6PM"
                  data-testid="handyman-hours-input"
                />
              </div>
              <div>
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                  placeholder="25"
                  min="0"
                  step="0.5"
                  data-testid="handyman-rate-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Skills (comma-separated)</Label>
                <Input
                  value={formData.skills}
                  onChange={(e) => setFormData({...formData, skills: e.target.value})}
                  placeholder="Plumbing, Electrical, HVAC"
                  data-testid="handyman-skills-input"
                />
              </div>
              <div className="col-span-2">
                <Label>Off Days (comma-separated)</Label>
                <Input
                  value={formData.off_days}
                  onChange={(e) => setFormData({...formData, off_days: e.target.value})}
                  placeholder="Saturday, Sunday"
                  data-testid="handyman-offdays-input"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-[#595959] hover:bg-[#404040]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Handyman
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Handyman Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#595959]" />
              Edit Handyman
            </DialogTitle>
            <DialogDescription>
              Update technician details and credentials.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditHandyman} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 555-123-4567"
                />
              </div>
              <div className="col-span-2">
                <Label>New Password (leave blank to keep current)</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Enter new password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Only fill if you want to change the password</p>
              </div>
              <div>
                <Label>Working Hours</Label>
                <Input
                  value={formData.working_hours}
                  onChange={(e) => setFormData({...formData, working_hours: e.target.value})}
                  placeholder="9AM-6PM"
                />
              </div>
              <div>
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                  placeholder="25"
                  min="0"
                  step="0.5"
                />
              </div>
              <div className="col-span-2">
                <Label>Skills (comma-separated)</Label>
                <Input
                  value={formData.skills}
                  onChange={(e) => setFormData({...formData, skills: e.target.value})}
                  placeholder="Plumbing, Electrical, HVAC"
                />
              </div>
              <div className="col-span-2">
                <Label>Off Days (comma-separated)</Label>
                <Input
                  value={formData.off_days}
                  onChange={(e) => setFormData({...formData, off_days: e.target.value})}
                  placeholder="Saturday, Sunday"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-[#595959] hover:bg-[#404040]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Handyman
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedHandyman?.name}</strong>? This will also delete their user account. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteHandyman} 
              disabled={saving}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
